import { PayloadAction } from '@reduxjs/toolkit';
import {
  LS_IS_CAREGIVER_WAITING_FOR_APPROVAL,
  LS_ITEM_ACCESS_TOKEN,
  LS_ITEM_IS_LOGGED_IN,
  LS_ITEM_OIDC_TOKEN,
  LS_ITEM_REFRESH_TOKEN,
} from 'store/constants';
import { RootState } from 'store/store';
import { createSliceWithStateReset } from 'store/createSliceWithStateReset';
import { getInitialAuthState, updateIsLoggedInLocalStorage } from 'utils/authUtils';
import type { OidcConfig, UpdateAuthTokens } from './types';

const authSlice = createSliceWithStateReset({
  name: 'auth',
  // Using a function here ensures we always fetch the latest localStorage during initialisation.
  initialState: getInitialAuthState,
  reducers: {
    refreshAuthState: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>,
    ) => {
      localStorage.setItem(LS_ITEM_ACCESS_TOKEN, action.payload.accessToken);
      localStorage.setItem(LS_ITEM_REFRESH_TOKEN, action.payload.refreshToken);
      return {
        ...state,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
      };
    },
    setCredentials: (
      state,
      { payload: { accessToken, refreshToken } }: PayloadAction<UpdateAuthTokens>,
    ) => {
      localStorage.setItem(LS_ITEM_ACCESS_TOKEN, accessToken);
      localStorage.setItem(LS_ITEM_REFRESH_TOKEN, refreshToken);
      return {
        ...state,
        refreshToken,
        accessToken,
      };
    },
    clearAuth: (state) => ({
      ...state,
      isLoggedIn: false,
      refreshToken: null,
      accessToken: null,
      hospitalLogoUrl: undefined,
      user: null,
      oidcConfig: null,
    }),
    setUser: (state, action) => ({
      ...state,
      user: action.payload,
    }),
    setOidcConfig: (state, action: PayloadAction<OidcConfig | null>) => {
      // Store in localStorage
      localStorage.setItem(LS_ITEM_OIDC_TOKEN, JSON.stringify(action.payload));
      return {
        ...state,
        oidcConfig: action.payload,
      };
    },
    setIsLoggedIn: (state, action: PayloadAction<boolean>) => {
      updateIsLoggedInLocalStorage(action.payload);
      return {
        ...state,
        isLoggedIn: action.payload,
      };
    },
    setIsCaregiverWaitingForApproval: (
      state,
      action: PayloadAction<{ value: boolean; persist?: boolean }>,
    ) => {
      const { value, persist } = action.payload;
      if (persist) {
        localStorage.setItem(LS_IS_CAREGIVER_WAITING_FOR_APPROVAL, value.toString());
      }
      return {
        ...state,
        isCaregiverPendingApproval: value,
      };
    },
    setIsUserDoneIneligible: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isUserDoneIneligible: action.payload,
    }),
    setHospitalLogoUrl: (state, action: PayloadAction<string | undefined>) => ({
      ...state,
      hospitalLogoUrl: action.payload,
    }),
  },
});

export const {
  setCredentials,
  refreshAuthState,
  clearAuth,
  setUser,
  setOidcConfig,
  setIsLoggedIn,
  setIsCaregiverWaitingForApproval,
  setIsUserDoneIneligible,
  setHospitalLogoUrl,
} = authSlice.actions;

export const { reducer: authReducer } = authSlice;

export const selectAuthAccessToken = (state: RootState) => state.auth.accessToken;

export const selectUser = (state: RootState) => state.auth.user;

export const selectOidcConfig = (state: RootState) => state.auth.oidcConfig;

export const selectIsLoggedIn = (state: RootState) => state.auth.isLoggedIn;

export const selectIsCaregiverPendingApproval = (state: RootState) =>
  state.auth.isCaregiverPendingApproval;

export const selectRefreshToken = (state: RootState) => state.auth.refreshToken;

export const selectIsUserDoneIneligible = (state: RootState) => state.auth.isUserDoneIneligible;

export const selectHospitalLogoUrl = (state: RootState) => state.auth.hospitalLogoUrl;
